import {
  Github,
  Sun,
  Moon,
  Heart,
  Menu,
  Search,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import type { ToolDef } from '../../tools';

interface Props {
  tool: ToolDef;
  groupLabel?: string;
  isSidebarCollapsed: boolean;
  onToggleSidebar: () => void;
  onOpenMobileNav: () => void;
  onOpenPalette: () => void;
}

export default function Header({
  tool,
  groupLabel,
  isSidebarCollapsed,
  onToggleSidebar,
  onOpenMobileNav,
  onOpenPalette,
}: Props) {
  const { theme, toggleTheme } = useTheme();
  const Icon = tool.icon;
  return (
    <header className="sticky top-0 z-20 border-b border-slate-200/90 bg-white/70 backdrop-blur-xl dark:border-white/[0.06] dark:bg-navy-950/60">
      <div className="px-4 lg:px-6 py-3 flex items-center gap-3">
        <button
          onClick={onOpenMobileNav}
          className="icon-btn md:hidden"
          title="Open navigation"
          aria-label="Open navigation"
        >
          <Menu className="w-4 h-4" />
        </button>
        <button
          onClick={onToggleSidebar}
          className="icon-btn hidden md:inline-flex"
          title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={
            isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'
          }
        >
          {isSidebarCollapsed ? (
            <PanelLeftOpen className="w-4 h-4" />
          ) : (
            <PanelLeftClose className="w-4 h-4" />
          )}
        </button>
        <div className="hidden sm:grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-sky-500/20 bg-gradient-to-br from-sky-500/10 to-indigo-500/10 text-sky-500 dark:text-sky-300">
          <Icon className="w-[18px] h-[18px]" />
        </div>
        <div className="min-w-0">
          {groupLabel && (
            <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-slate-400 dark:text-slate-500 truncate">
              {groupLabel}
            </div>
          )}
          <h1 className="font-display font-bold leading-tight tracking-tight text-slate-900 dark:text-white truncate">
            {tool.name}
          </h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
            {tool.description}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          <button
            onClick={onOpenPalette}
            className="hidden sm:inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white/60 px-3 py-1.5 text-xs font-medium text-slate-500 transition-all duration-200 hover:border-sky-400/50 hover:text-slate-700 dark:border-white/10 dark:bg-white/[0.04] dark:text-slate-400 dark:hover:border-sky-400/40 dark:hover:text-slate-200"
            title="Jump to a tool (Ctrl+K)"
            aria-label="Open tool search"
          >
            <Search className="w-3.5 h-3.5" />
            <span>Jump to tool</span>
            <span className="kbd">Ctrl</span>
            <span className="kbd">K</span>
          </button>
          <button
            onClick={onOpenPalette}
            className="icon-btn sm:hidden"
            title="Jump to a tool"
            aria-label="Open tool search"
          >
            <Search className="w-4 h-4" />
          </button>
          <a
            href="https://github.com/Pavan-29/All-in-one-tools-for-CPI"
            target="_blank"
            rel="noreferrer"
            className="icon-btn"
            title="View source"
          >
            <Github className="w-4 h-4" />
          </a>
          <button
            onClick={toggleTheme}
            className="icon-btn"
            title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? (
              <Sun className="w-4 h-4" />
            ) : (
              <Moon className="w-4 h-4" />
            )}
          </button>
          <span
            className="hidden lg:inline-flex ml-2 items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400"
            title="Made for cloud integration developers"
          >
            <Heart className="w-3 h-3 text-rose-400" /> for integration devs
          </span>
        </div>
      </div>
    </header>
  );
}
